import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserGroupIcon, DocumentTextIcon, CreditCardIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import API_URL from '../config/apiConfig';

const AdminOverview = () => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeUsers: 0,
        totalGenerations: 0,
        averageQuota: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoading(true);
        try {
            // Dans un environnement réel, cette requête devrait inclure un token d'authentification
            const response = await axios.get(`${API_URL}/admin/stats`, {
                params: { adminKey: process.env.REACT_APP_ADMIN_KEY }
            });

            if (response.data.success) {
                setStats(response.data.data);
            } else {
                console.error('Erreur lors de la récupération des statistiques:', response.data.message);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des statistiques:', error);
            // Valeurs fictives pour la démo
            setStats({
                totalUsers: 52,
                activeUsers: 38,
                totalGenerations: 1287,
                averageQuota: 33
            });
        } finally {
            setLoading(false);
        }
    };

    // Array des statistiques pour les cartes
    const statCards = [
        {
            title: 'Utilisateurs totaux',
            value: stats.totalUsers,
            icon: UserGroupIcon,
            color: 'bg-blue-500',
            link: '/admin/users'
        },
        {
            title: 'Utilisateurs actifs',
            value: stats.activeUsers,
            icon: UserGroupIcon,
            color: 'bg-green-500',
            link: '/admin/users'
        },
        {
            title: 'PDF générés',
            value: stats.totalGenerations,
            icon: DocumentTextIcon,
            color: 'bg-purple-500',
            link: '/admin/stats'
        },
        {
            title: 'Quota moyen',
            value: stats.averageQuota,
            icon: ChartBarIcon,
            color: 'bg-yellow-500',
            link: '/admin/quotas'
        }
    ];

    return (
        <div>
            <h1 className="text-2xl font-semibold text-gray-700 mb-6">Tableau de bord</h1>

            {loading ? (
                <div className="text-center py-6">
                    <p className="text-gray-500">Chargement des statistiques...</p>
                </div>
            ) : (
                <>
                    {/* Cartes de statistiques */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {statCards.map((stat, index) => (
                            <Link
                                key={index}
                                to={stat.link}
                                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-300"
                            >
                                <div className="px-4 py-5 sm:p-6">
                                    <div className="flex items-center">
                                        <div className={`flex-shrink-0 rounded-md p-3 ${stat.color}`}>
                                            <stat.icon className="h-6 w-6 text-white" />
                                        </div>
                                        <div className="ml-5 w-0 flex-1">
                                            <dt className="text-sm font-medium text-gray-500 truncate">
                                                {stat.title}
                                            </dt>
                                            <dd className="flex items-baseline">
                                                <div className="text-2xl font-semibold text-gray-900">
                                                    {stat.value}
                                                </div>
                                            </dd>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Accès rapides */}
                    <div className="bg-white shadow rounded-lg p-6 mb-8">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Actions rapides</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Link
                                to="/admin/users"
                                className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                            >
                                <UserGroupIcon className="h-8 w-8 text-blue-500" />
                                <div className="ml-4">
                                    <p className="text-base font-medium text-gray-900">Gérer les utilisateurs</p>
                                    <p className="text-sm text-gray-500">Ajouter, modifier ou supprimer des utilisateurs</p>
                                </div>
                            </Link>

                            <Link
                                to="/admin/quotas"
                                className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                            >
                                <DocumentTextIcon className="h-8 w-8 text-purple-500" />
                                <div className="ml-4">
                                    <p className="text-base font-medium text-gray-900">Gérer les quotas</p>
                                    <p className="text-sm text-gray-500">Ajuster les quotas des utilisateurs</p>
                                </div>
                            </Link>

                            <Link
                                to="/admin/payments"
                                className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                            >
                                <CreditCardIcon className="h-8 w-8 text-green-500" />
                                <div className="ml-4">
                                    <p className="text-base font-medium text-gray-900">Gérer les paiements</p>
                                    <p className="text-sm text-gray-500">Voir et gérer les transactions</p>
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* Section informative */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Pour commencer</h2>
                        <p className="text-gray-600 mb-4">
                            Bienvenue dans votre panneau d'administration. Utilisez les différentes sections pour gérer vos utilisateurs,
                            surveiller les quotas de génération de PDF, et suivre les statistiques d'utilisation.
                        </p>
                        <p className="text-gray-600">
                            Pour toute question ou assistance, contactez le support technique.
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminOverview;